import { useState, type FormEvent } from 'react';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatCompetencia, formatDateTime, parseMoneyInput, toInputMoney } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface Goal {
  competencia: string;
  metaIndividual: string;
  metaGlobal: string;
  padrao: boolean;
  definidaPor: string | null;
  atualizadaEm: string | null;
  fechado: boolean;
}

/** Metas do mês, iguais para todos os vendedores. */
export function GoalsPage() {
  const { data, error, reload } = useApi<Goal[]>('/goals');
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Metas</h1>
          <p className="muted">A meta individual vale para todos os vendedores. A meta da equipe libera o bônus coletivo.</p>
        </div>
      </header>
      {error && <Alert>{error}</Alert>}
      <section className="card flush">
        {!data ? (
          <p className="muted pad">Carregando…</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Mês</th>
                  <th scope="col" className="num">Meta individual</th>
                  <th scope="col" className="num">Meta da equipe</th>
                  <th scope="col">Definida por</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((g) =>
                  editing === g.competencia ? (
                    <tr key={g.competencia}>
                      <td colSpan={5}>
                        <GoalForm
                          goal={g}
                          onCancel={() => setEditing(null)}
                          onSaved={() => {
                            setEditing(null);
                            void reload();
                          }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={g.competencia}>
                      <td>
                        <strong>{formatCompetencia(g.competencia)}</strong>
                        {g.fechado && <span className="tag">fechado</span>}
                      </td>
                      <td className="num">{formatBRL(g.metaIndividual)}</td>
                      <td className="num">{formatBRL(g.metaGlobal)}</td>
                      <td className="small">{g.padrao ? <span className="muted">Padrão (não definida)</span> : `${g.definidaPor ?? '—'} · ${formatDateTime(g.atualizadaEm)}`}</td>
                      <td className="actions">
                        {!g.fechado && (
                          <Button variant="secondary" onClick={() => setEditing(g.competencia)}>
                            {g.padrao ? 'Definir' : 'Alterar'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function GoalForm({ goal, onCancel, onSaved }: { goal: Goal; onCancel: () => void; onSaved: () => void }) {
  const [ind, setInd] = useState(toInputMoney(goal.metaIndividual));
  const [glob, setGlob] = useState(toInputMoney(goal.metaGlobal));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const metaIndividual = parseMoneyInput(ind);
    const metaGlobal = parseMoneyInput(glob);
    if (!metaIndividual || !metaGlobal) return setError('Valor inválido. Use o formato 100.000,00.');
    setSaving(true);
    setError(null);
    try {
      await api.put(`/goals/${goal.competencia}`, { metaIndividual, metaGlobal });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="grid-form" onSubmit={(e) => void submit(e)}>
      <strong className="grid-form__title">{formatCompetencia(goal.competencia)}</strong>
      {error && <Alert>{error}</Alert>}
      <div className="field">
        <label htmlFor="meta-ind">Meta individual (R$)</label>
        <input id="meta-ind" inputMode="decimal" value={ind} onChange={(e) => setInd(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="meta-glob">Meta da equipe (R$)</label>
        <input id="meta-glob" inputMode="decimal" value={glob} onChange={(e) => setGlob(e.target.value)} required />
      </div>
      <div className="row">
        <Button type="submit" loading={saving}>
          Salvar meta
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
